import classnames from 'classnames/bind'
import dynamic from 'next/dynamic'
import { useEffect, useMemo, useTransition } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { TRACKING_EVENTS } from '~/lib/constants'
import { getEANSFromVariants } from '~/lib/eans'
import linkResolver from '~/lib/link-resolver'
import { SHOPIFY_CUSTOM_ATTRIBUTES } from '~/lib/shopify/constants'

import { UnlikelyCart } from '@unlikelystudio/commerce-connector'

import LoadingComponent from '~/components/Abstracts/LoadingComponent'
import PanelSizeGuide from '~/components/Panels/SizeGuideWrapped'
import AddToCartButton from '~/components/UI/AddToCartButton'
import ColorsProductHeader from '~/components/UI/ColorsProductHeader'
import { DiscountBadge } from '~/components/UI/DiscountBadge'
import InlineCta from '~/components/UI/InlineCta'
import ProductAccordion from '~/components/UI/ProductHeader/ProductAccordion'
import KlarnaLabel from '~/components/UI/ProductHeader/ProductDetails/KlarnaLabel'
import ProductPromo from '~/components/UI/ProductHeader/ProductPromo'
import { ProductSetDetails } from '~/components/UI/ProductHeader/ProductSetDetails'
import ProductStickyCta from '~/components/UI/ProductHeader/ProductStickyCta'
import ProductVariants from '~/components/UI/ProductHeader/ProductVariants'
import ProductSliderReassurance from '~/components/UI/ProductSliderReassurance'
import SquaredCta from '~/components/UI/SquaredCta'

import { useNavigation } from '~/providers/NavigationProvider'
import { usePanel } from '~/providers/PanelProvider'
import { usePopIn } from '~/providers/PopInProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import useCartItems from '~/hooks/cart/useCartItems'
import { getCartTrackingDataFromCart } from '~/hooks/tracking/utils'
import useCurrency from '~/hooks/useCurrency'
import { useHasReachMaxAvailableQuantity } from '~/hooks/useHasReachMaxAvailableQuantity'
import useLocale from '~/hooks/useLocale'
import useShop from '~/hooks/useShop'

import getAlternateUrl from '~/utils/alternate-url'
import {
  getIsProductFragrance,
  getIsProductSet,
} from '~/utils/get-shopify-product-type'

import { CTA, PRODUCT, SIZE_GUIDE } from '~/data/dictionary'
import serializePrice from '~/data/serialize-price'

import { ProductHeaderProps } from '../types'
import css from './styles.module.scss'

const PopIn = dynamic(() => import('~/components/UI/Popin'), {
  loading: () => <LoadingComponent />,
})

const AlertStockPopin = dynamic(
  () => import('~/components/Popins/AlertStockPopin'),
  {
    loading: () => <LoadingComponent />,
  },
)

const cx = classnames.bind(css)

function ProductDetails({
  className,
  name,
  subtitle,
  variants,
  accordions,
  colorVariants,
  selectedVariant,
  productNearestStore,
  productDiscount,
  productCMSPrice,
  type,
  uid,
  image,
  shopifyId,
  productType,
  sample,
  triggerOnLoadEvent,
  images,
  sizeGuides,
  productIsGiftCard,
  sizeInformation,
  setInformations,
  productPromo,
}: ProductHeaderProps) {
  const [isPending, startTransition] = useTransition()
  const t = useTranslate()
  const locale = useLocale()
  const currency = useCurrency()
  const { tracker } = useTracker()

  const { add, removeCurrent } = usePopIn()
  const { add: addPanel } = usePanel()
  const openPanel = () => {
    startTransition(() => {
      addPanel({
        component: <PanelSizeGuide data={sizeGuides} />,
      })
    })
  }

  const nameStyle = useStyle({
    textPreset: GlobalTextPreset.Title22Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const subtitleStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Haffer,
    color: GlobalThemeColors.DoveGray,
  })

  const sizeInformationStyles = useStyle({
    textPreset: GlobalTextPreset.Label12Haffer,
    color: GlobalThemeColors.DoveGray,
  })

  const pricesItemStyle = useStyle({
    textPreset: GlobalTextPreset.Label12Haffer,
  })

  /* It's a hook that returns if the current prismic type can be shopped or not */
  const shop = useShop(type)

  // Swaven EANS processed from variants
  const processedEANS = getEANSFromVariants(variants)

  const processedPrice = productCMSPrice
    ? productCMSPrice
    : selectedVariant?.priceAmount

  const crossedOutPrice =
    serializePrice(locale, currency, selectedVariant?.compareAtPriceAmount) ??
    null

  const price = useMemo(
    () =>
      /* Overload the shopify price by CMS value for product sets */
      serializePrice(locale, currency, processedPrice),
    [locale, currency, selectedVariant, productCMSPrice],
  )

  const memoizedVariantName = useMemo(
    () => selectedVariant?.title,
    [selectedVariant?.title],
  )

  const [cartItems, productsTracking, sampleTracking] = useCartItems({
    type,
    uid,
    name,
    image: images?.[0] ?? null,
    productCMSPrice,
    selectedVariant,
    shopifyId,
    productType,
    sample,
    images,
    colorVariants,
    productIsGiftCard,
  })

  const trackingPayloadDetail = {
    eventLabel: name,
    ecommerce: {
      currencyCode: currency,
      detail: {
        products: productsTracking,
      },
    },
  }

  const trackingPayloadAddToCart = {
    eventLabel: name,
    ecommerce: {
      currencyCode: currency,
      add: {
        products: productsTracking,
      },
    },
  }

  const availableForSale = useMemo(
    () => selectedVariant?.availableForSale,
    [selectedVariant, type],
  )

  const showStockAlertPopin = () => {
    startTransition(() => {
      add(
        <PopIn className={cx(css.popin)}>
          <AlertStockPopin
            className={cx(css.alertStock)}
            variant={selectedVariant}
            productName={name}
            productImage={image}
            onLabelLinkClick={() => removeCurrent()}
            redirectLink={getAlternateUrl(
              linkResolver({ type, uid }, locale, true),
            )}
          />
        </PopIn>,
      )
    })
  }

  useEffect(() => {
    if (!triggerOnLoadEvent) return
    /* Tiemout in order to send it after the routing pageview event */
    setTimeout(
      () => tracker.emit(TRACKING_EVENTS.PRODUCT_DETAIL, trackingPayloadDetail),
      500,
    )
  }, [memoizedVariantName])

  const handleAddToCart = (data: UnlikelyCart) => {
    const cartDataPayload = getCartTrackingDataFromCart(data)

    // Getting the tracking data consolidated with the cart
    const fullTrackingData = {
      ...trackingPayloadAddToCart,
      ecommerce: {
        ...trackingPayloadAddToCart.ecommerce,
        ...cartDataPayload,
      },
    }

    tracker.emit(TRACKING_EVENTS.ADD_TO_CART, fullTrackingData)

    sampleTracking?.forEach((sample) => {
      tracker.emit(TRACKING_EVENTS.ADD_SAMPLES, { eventLabel: sample?.name })
    })
  }

  const {
    ref: primaryAddToCartInViewRef,
    inView: isPrimaryAddToCartInView,
    entry,
  } = useInView({
    threshold: 0.2,
  })

  const isPrimaryAddToCartHidded =
    (!isPrimaryAddToCartInView && entry?.boundingClientRect?.top < 0) ?? false

  const { footerVisible } = useNavigation()

  const hasReachMaxAvailableQuantity = useHasReachMaxAvailableQuantity(
    cartItems?.map((item) => item?.merchandiseId),
  )

  const defaultOpenedAccordionIndex =
    getIsProductFragrance(type) || getIsProductSet(type)
      ? 0
      : accordions?.length - 1

  return (
    <div className={cx(className, css.ProductDetails)}>
      <div className={css.content}>
        {name && (
          <div className={css.name}>
            {selectedVariant?.computedDiscountPercentage && (
              <DiscountBadge
                className={css.discountBadge}
                value={`${t(PRODUCT.DISCOUNT)} ${
                  selectedVariant?.computedDiscountPercentage
                }`}
              />
            )}
            <h1 className={cx(css.nameText, nameStyle)}>{name}</h1>
          </div>
        )}
        {subtitle && (
          <h2 className={cx(css.subtitle, subtitleStyle)}>{subtitle}</h2>
        )}
        {selectedVariant?.compareAtPrice && selectedVariant?.price && (
          <div className={css.prices}>
            <span
              className={cx(
                css.pricesItem,
                pricesItemStyle,
                css.compareAtPriceItem,
              )}>
              {selectedVariant?.compareAtPrice}
            </span>
            <span className={cx(css.pricesItem, pricesItemStyle)}>
              {selectedVariant?.price}
            </span>
          </div>
        )}
        {/* If Fashion type add colors variants */}
        {colorVariants?.length > 0 && (
          <ColorsProductHeader items={colorVariants} />
        )}
        {/* Sets display */}
        {getIsProductSet(type) && (
          <ProductSetDetails
            className={css.productSetDetails}
            productDiscount={productDiscount}
            setInformations={setInformations}
          />
        )}
        {(sizeInformation || sizeGuides?.length > 0) && (
          <div
            className={cx(css.sizeInformationAndGuideContainer, {
              [css.hasSizeInformation]: sizeInformation,
            })}>
            {sizeInformation && (
              <span className={cx(css.sizeInformation, sizeInformationStyles)}>
                {sizeInformation}
              </span>
            )}
            {sizeGuides?.length > 0 && (
              <InlineCta
                onClick={openPanel}
                className={css.sizeGuideButton}
                textStyling={GlobalTextStyling.UpperCase}
                textPreset={GlobalTextPreset.Cta12Haffer}>
                {t(SIZE_GUIDE.TITLE)}
              </InlineCta>
            )}
          </div>
        )}
        <ProductVariants
          type={type}
          className={cx(css.productVariants, css?.[`variants_${type}`])}
          active={selectedVariant}
          variants={variants}
        />
        {/* If no ecom version add show now cta with swaven integration */}
        {!shop && (
          <SquaredCta
            className={cx(css.mainCta, 'swn-tag-wtb-btn', { isShopNow: true })}
            data-eans={processedEANS?.join(',')}
            isLoading={isPending}>
            {t(PRODUCT.SHOP_NOW)}
          </SquaredCta>
        )}
        {/* If ecom version button to add to cart */}
        {shop && availableForSale && !hasReachMaxAvailableQuantity && (
          <>
            <span
              className={css.addToCartMarker}
              ref={primaryAddToCartInViewRef}
            />
            <AddToCartButton
              className={cx(css.mainCta)}
              disabled={!price}
              price={price}
              isGiftCard={productIsGiftCard}
              crossedOutPrice={crossedOutPrice}
              cartItems={cartItems}
              onAdded={(data) => {
                handleAddToCart(data)
              }}
            />
          </>
        )}
        {shop && (!availableForSale || hasReachMaxAvailableQuantity) && (
          <SquaredCta
            className={cx(css.mainCta, css.stockAlertCta)}
            onClick={showStockAlertPopin}
            isLoading={isPending}>
            <span>{t(CTA.OUT_OF_STOCK)}</span>
          </SquaredCta>
        )}

        {/* If ecom version add nearest store  */}
        {shop && productNearestStore && (
          <InlineCta
            textPreset={GlobalTextPreset.Cta12Haffer}
            textStyling={GlobalTextStyling.UpperCase}
            className={cx(css.productNearestStore, 'swn-tag-wtb-btn')}
            data-eans={processedEANS?.join(',')}>
            {productNearestStore.children}
          </InlineCta>
        )}

        <KlarnaLabel type={type} className={css.klarnaWrapper} />

        {shop && productPromo && <ProductPromo data={productPromo} />}

        {shop && (
          <ProductSliderReassurance
            className={css.productReassurance}
            type={type}
          />
        )}

        <ProductAccordion
          className={cx(css.accordion)}
          accordions={accordions}
          index={defaultOpenedAccordionIndex}
        />
        {/* If mobile version add the sticky cta */}
        {shop && availableForSale && !hasReachMaxAvailableQuantity && (
          <ProductStickyCta
            className={cx(css.productStickyCta, {
              active: isPrimaryAddToCartHidded && !footerVisible,
            })}
            isDisplayed={isPrimaryAddToCartHidded && !footerVisible}
            images={images}
            name={name}
            subtitle={subtitle}
            price={price}
            variants={variants}
            selectedVariant={selectedVariant}
            cartItems={cartItems}
            crossedOutPrice={crossedOutPrice}
            handleAddToCart={(data) => handleAddToCart(data)}
            isGiftCard={productIsGiftCard}
            hasOnlyAddToCartButton={getIsProductSet(type)}
          />
        )}
      </div>
    </div>
  )
}

ProductDetails.defaultProps = {}

export default ProductDetails
